import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ContactButton } from "@/components/trial/UpgradeCard";
import { Clock, CheckCircle2, Lock, Users, BarChart3, KeyRound } from "lucide-react";
import { toast } from "sonner";

export function TrialStatusPanel({ classes }) {
  const { user, refresh } = useAuth();
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const redeem = async () => {
    setRedeeming(true);
    try {
      await api.post("/auth/redeem-invite", { code: code.trim() });
      await refresh();
      toast.success("Welcome aboard — your full Guide account is unlocked! Your classes and student progress are kept.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && d.message) || (typeof d === "string" ? d : "That invite code didn't work."));
    } finally { setRedeeming(false); }
  };
  return (
    <div className="hq-glass rounded-2xl p-6 border-t border-t-primary/40" data-testid="trial-status-panel">
      <p className="text-[10px] uppercase tracking-widest text-primary font-mono-data">Free trial</p>
      <h2 className="font-display text-2xl mt-1 flex items-center gap-2"><Clock className="w-5 h-5 text-primary" /> {user.trial_days_left} day{user.trial_days_left === 1 ? "" : "s"} left</h2>
      <ul className="mt-4 space-y-1.5 text-sm text-slate-300">
        <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#34D399]" /> Documents Blocks 1–3</li>
        <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#34D399]" /> Mid-Unit Task Test</li>
        <li className="flex items-center gap-2"><Users className="w-4 h-4 text-[#22D3EE]" /> {classes}/{user.trial_max_classes} classes created</li>
        <li className="flex items-center gap-2 text-slate-400"><Lock className="w-4 h-4" /> Blocks 4–5, Unit Tests, Spreadsheets, Presentations, Email</li>
      </ul>
      <p className="text-xs text-muted-foreground mt-3">Share a class join code with your students — they'll get the same trial content, graded into your Reports.</p>
      <ContactButton contact={user.trial_contact} user={user} className="mt-4 w-full text-sm" label="Unlock the full HorizonQuest" />
      <div className="mt-4 pt-4 border-t border-white/10">
        <p className="text-xs text-slate-300 flex items-center gap-1.5 mb-2"><KeyRound className="w-3.5 h-3.5 text-[#22D3EE]" /> Have an invite code?</p>
        <div className="flex gap-2">
          <input data-testid="trial-invite-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="8-character code" maxLength={8}
            className="flex-1 h-9 rounded-md bg-white/5 border border-white/15 px-3 text-sm font-mono-data tracking-widest uppercase outline-none focus:border-[#22D3EE]" />
          <button data-testid="trial-invite-submit" disabled={redeeming || code.trim().length < 4} onClick={redeem}
            className="h-9 px-3 rounded-md bg-[#22D3EE] text-[#04121f] text-sm font-medium hover:bg-[#67E8F9] disabled:opacity-50">{redeeming ? "…" : "Unlock"}</button>
        </div>
      </div>
    </div>
  );
}

export function TrialSignupsPanel() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/admin/trials").then((r) => setData(r.data)).catch(() => setData(null)); }, []);
  if (!data) return null;
  return (
    <div className="hq-glass rounded-2xl p-6 border-t border-t-primary/40" data-testid="trial-signups-panel">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-primary font-mono-data">Free-trial signups</p>
          <h2 className="font-display text-3xl mt-1" data-testid="trial-signups-total">{data.total_signups}</h2>
          <p className="text-xs text-muted-foreground">{data.active} active right now</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Link to="/admin/analytics" data-testid="open-analytics-link" className="inline-flex items-center gap-1 text-xs text-[#22D3EE] hover:underline"><BarChart3 className="w-3.5 h-3.5" /> View analytics</Link>
          <button data-testid="trial-signups-toggle" onClick={() => setOpen((o) => !o)} className="text-xs text-primary hover:underline">{open ? "Hide" : "Show list"}</button>
        </div>
      </div>
      {open && (
        <ul className="mt-4 space-y-2 max-h-72 overflow-auto pr-1">
          {data.trials.length === 0 && <li className="text-sm text-muted-foreground">No trial teachers yet.</li>}
          {data.trials.map((t) => (
            <li key={t.user_id} data-testid={`trial-row-${t.user_id}`} className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium truncate">{t.name || t.email}</span>
                <span className={`text-[11px] font-mono-data shrink-0 ${t.expired ? "text-rose-400" : "text-[#34D399]"}`}>{t.expired ? "ended" : `${t.days_left}d left`}</span>
              </div>
              <p className="text-[11px] text-muted-foreground truncate">{t.email}{t.school ? ` · ${t.school}` : ""} · {t.classes} classes · {t.students} students</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
